/* Optispar Motion V1
   Lightweight, one-shot motion and interaction controller for the draft theme.
   Structural content stays visible without JavaScript. No animation library. */
(function () {
  'use strict';

  var VERSION = '1.2.3';
  var SECTION_SELECTOR = '.shopify-section';
  var REVIEW_WIDGET_SELECTOR = '.reputon-ebay-reviews-widget';
  var REVIEW_SECTION_HINT = '1771092290e52e1a50';
  var REVIEW_INITIAL = 10;
  var REVIEW_BATCH = 10;
  var REVIEW_MAX = 50;
  var TOPBAR_INTERVAL = 5000;
  var TOPBAR_TOUCH_PAUSE = 6500;
  var REVEAL_DURATION = 260;
  var REVEAL_DISTANCE = 10;
  var REVEAL_STAGGER = 35;
  var REVEAL_EASING = 'cubic-bezier(.22, 1, .36, 1)';

  if (
    window.OptisparMotion &&
    typeof window.OptisparMotion.destroy === 'function'
  ) {
    window.OptisparMotion.destroy();
  }

  var reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );
  var connection = navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null;
  var globalAbort = new AbortController();
  var sectionStates = new Map();
  var observedTargets = new WeakMap();
  var revealInlineSnapshots = new WeakMap();
  var destroyed = false;
  var documentElement = document.documentElement;
  var initialSaveDataClass = documentElement.classList.contains('os-save-data');
  var initialSaveDataAttribute = documentElement.getAttribute('data-os-save-data');

  function saveDataEnabled() {
    return Boolean(connection && connection.saveData);
  }

  function decorativeMotionDisabled() {
    return reducedMotionQuery.matches || saveDataEnabled();
  }

  function syncSaveDataState() {
    var enabled = saveDataEnabled();
    documentElement.classList.toggle('os-save-data', enabled);
    if (enabled) documentElement.setAttribute('data-os-save-data', 'true');
    else documentElement.removeAttribute('data-os-save-data');
  }

  function requestFrame(state, callback) {
    var frame = window.requestAnimationFrame(function () {
      state.frames.delete(frame);
      if (!state.destroyed) callback();
    });
    state.frames.add(frame);
    return frame;
  }

  function setTimer(state, callback, delay) {
    var timer = window.setTimeout(function () {
      state.timers.delete(timer);
      if (!state.destroyed) callback();
    }, delay);
    state.timers.add(timer);
    return timer;
  }

  function clearTimer(state, timer) {
    if (!timer) return;
    window.clearTimeout(timer);
    state.timers.delete(timer);
  }

  function createSectionState(root) {
    return {
      root: root,
      abort: new AbortController(),
      frames: new Set(),
      timers: new Set(),
      observed: new Set(),
      revealElements: new Set(),
      revealDelays: new Map(),
      revealOrigins: new Map(),
      cleanups: [],
      review: null,
      destroyed: false
    };
  }

  function observe(target, descriptor) {
    if (!viewportObserver || !target || !target.isConnected) {
      if (descriptor.kind === 'reveal') {
        revealElement(descriptor.state, target, false);
      }
      return;
    }
    observedTargets.set(target, descriptor);
    descriptor.state.observed.add(target);
    viewportObserver.observe(target);
  }

  function unobserve(state, target) {
    if (!target) return;
    if (viewportObserver) viewportObserver.unobserve(target);
    state.observed.delete(target);
    observedTargets.delete(target);
  }

  /* One observer is shared by all section reveals and review batch sentinels. */
  var viewportObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var descriptor = observedTargets.get(entry.target);
            if (!descriptor || descriptor.state.destroyed) return;

            if (descriptor.kind === 'review') {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
                descriptor.review.revealNextBatch();
              }
              return;
            }

            if (entry.isIntersecting && entry.intersectionRatio >= 0.12) {
              revealElement(descriptor.state, entry.target, true);
            }
          });
        },
        {
          root: null,
          rootMargin: '0px 0px -8% 0px',
          threshold: [0, 0.12, 0.55]
        }
      )
    : null;

  function snapshotRevealStyles(element) {
    if (revealInlineSnapshots.has(element)) return;
    var properties = [
      'opacity',
      'transform',
      'transition-property',
      'transition-duration',
      'transition-timing-function',
      'transition-delay'
    ];
    var snapshot = {};
    properties.forEach(function (property) {
      snapshot[property] = {
        value: element.style.getPropertyValue(property),
        priority: element.style.getPropertyPriority(property)
      };
    });
    revealInlineSnapshots.set(element, snapshot);
  }

  function clearRevealStyles(element) {
    if (!element) return;
    var snapshot = revealInlineSnapshots.get(element);
    if (snapshot) {
      Object.keys(snapshot).forEach(function (property) {
        var original = snapshot[property];
        if (original.value) {
          element.style.setProperty(property, original.value, original.priority);
        } else {
          element.style.removeProperty(property);
        }
      });
      revealInlineSnapshots.delete(element);
    }
    element.removeAttribute('data-os-motion-pending');
  }

  function revealTransform(origin) {
    if (origin === 'left') {
      return 'translate3d(-' + REVEAL_DISTANCE + 'px, 0, 0)';
    }
    if (origin === 'right') {
      return 'translate3d(' + REVEAL_DISTANCE + 'px, 0, 0)';
    }
    if (origin === 'top') {
      return 'translate3d(0, -8px, 0)';
    }
    return 'translate3d(0, 8px, 0)';
  }

  function revealElement(state, element, animate) {
    if (!element) return;
    unobserve(state, element);
    element.setAttribute('data-os-motion-revealed', 'true');

    if (!animate || decorativeMotionDisabled()) {
      clearRevealStyles(element);
      return;
    }

    element.style.setProperty(
      'transition-property',
      'opacity, transform',
      'important'
    );
    element.style.setProperty(
      'transition-duration',
      REVEAL_DURATION + 'ms',
      'important'
    );
    element.style.setProperty(
      'transition-timing-function',
      REVEAL_EASING,
      'important'
    );
    element.style.setProperty(
      'transition-delay',
      (state.revealDelays.get(element) || 0) + 'ms',
      'important'
    );

    var finished = false;
    var fallbackTimer = 0;
    var finish = function (event) {
      if (event && event.target !== element) return;
      if (finished) return;
      finished = true;
      clearTimer(state, fallbackTimer);
      element.removeEventListener('transitionend', finish);
      clearRevealStyles(element);
    };
    element.addEventListener('transitionend', finish, {
      once: true,
      signal: state.abort.signal
    });
    fallbackTimer = setTimer(state, finish, REVEAL_DURATION + 120);

    requestFrame(state, function () {
      element.style.setProperty('opacity', '1', 'important');
      element.style.setProperty(
        'transform',
        'translate3d(0, 0, 0)',
        'important'
      );
    });
  }

  function chooseRevealElement(root) {
    var inMain = root.parentElement && root.parentElement.matches('main, #MainContent');
    var isFooter = root.tagName === 'FOOTER' || /footer/i.test(root.id || '');
    if (!inMain && !isFooter) return null;

    var explicit = root.matches('[data-os-motion="section"], [data-os-motion="group"]')
      ? root
      : root.querySelector(
          ':scope > [data-os-motion="section"], :scope > [data-os-motion="group"]'
        );
    if (explicit) return explicit;

    /* Never transform a complete footer section: a transformed ancestor can
       temporarily change the containing block of chat/popover controls. */
    if (isFooter) return root.querySelector('.footer-content');

    return root.querySelector(':scope > .section .section-content-wrapper') ||
      root.querySelector(':scope > .section-content-wrapper') ||
      root.querySelector(':scope > .section') ||
      root;
  }

  function uniqueElements(elements) {
    var seen = new Set();
    return elements.filter(function (element) {
      if (!element || seen.has(element)) return false;
      seen.add(element);
      return true;
    });
  }

  function findRevealItems(root, selector, limit) {
    if (!root || !root.querySelectorAll) return [];
    return uniqueElements(Array.from(root.querySelectorAll(selector))).slice(
      0,
      limit
    );
  }

  function findPreferredRevealItems(root, selectors, limit) {
    var items = [];
    selectors.some(function (selector) {
      items = findRevealItems(root, selector, limit);
      return items.length > 0;
    });
    return items;
  }

  function alternatingOrigin(index) {
    return index % 2 === 0 ? 'left' : 'right';
  }

  function singleReveal(element, origin) {
    return [{ element: element, origin: origin || 'up', delay: 0 }];
  }

  function cardRevealPlan(root, heading, selectors) {
    var plan = heading
      ? [{ element: heading, origin: 'top', delay: 0 }]
      : [];
    var choices = Array.isArray(selectors) ? selectors : [selectors];
    findPreferredRevealItems(root, choices, 4).forEach(function (item, index) {
      plan.push({
        element: item,
        origin: alternatingOrigin(index),
        delay: index * REVEAL_STAGGER
      });
    });
    return plan;
  }

  /* The reveal matrix keeps motion meaningful instead of assigning random
     directions. Headings arrive from above, horizontal card rows enter from
     their nearest side, and product media/details mirror the page layout.
     Only the first four repeated items are staggered. */
  function automaticRevealPlan(state, fallback) {
    var root = state.root;
    var identity = ((root.id || '') + ' ' + (root.className || '')).toLowerCase();
    var plan = [];

    /* Long legal copy must be immediately readable and must never be hidden
       while an observer waits. Header/topbar and footer motion remain intact. */
    if (
      document.body.classList.contains('os-legal-page') &&
      root.closest &&
      root.closest('#MainContent')
    ) {
      return plan;
    }

    /* Hero media and primary copy are LCP-critical and must never wait for an
       observer or be hidden, even if the section is moved in the editor. */
    if (identity.indexOf('__hero_') !== -1 || findWithin(root, '[id^="Hero-"]')) {
      return plan;
    }

    var heading = findWithin(
      root,
      '.manual-products__header, .optispar-section-heading, .section-resource-list__content .text-block, .collection__title, .title-wrapper'
    );

    var cardSelectors = [];
    if (identity.indexOf('__collection_list_') !== -1) {
      cardSelectors = [
        '.resource-list__slide',
        '.resource-list__item',
        '.collection-card'
      ];
    } else if (
      identity.indexOf('__divider_') !== -1 ||
      findWithin(root, '.manual-products')
    ) {
      cardSelectors = ['product-card', '.product-card'];
    } else if (
      identity.indexOf('__section_4xbnpq') !== -1 ||
      findWithin(root, '.accordion .details')
    ) {
      cardSelectors = ['.accordion .details'];
    }
    if (cardSelectors.length) {
      plan = cardRevealPlan(root, heading, cardSelectors);
      return plan.length ? plan : singleReveal(fallback);
    }

    if (
      identity.indexOf('__1771092290e52e1a50') !== -1 ||
      findWithin(root, REVIEW_WIDGET_SELECTOR)
    ) {
      return singleReveal(fallback);
    }

    if (
      identity.indexOf('__marquee_') !== -1 ||
      root.classList.contains('optispar-trust-strip')
    ) {
      return singleReveal(fallback);
    }

    if (identity.indexOf('__section_ytvg3q') !== -1) {
      return singleReveal(fallback, 'left');
    }

    /* The main product purchase area must stay visually anchored while the
       customer scrolls. Revealing the whole gallery/details pair adds a second
       movement on top of native page scrolling and makes the page feel jumpy. */
    if (
      root.closest('#MainContent[data-template="product"]') &&
      findWithin(root, ':scope > .product-information')
    ) {
      return [];
    }

    var productMedia = findWithin(
      root,
      'media-gallery, .product-media-gallery, .product__media-gallery'
    );
    var productDetails = findWithin(
      root,
      '.product-information, .product__info-wrapper, product-information'
    );
    if (productMedia || productDetails) {
      if (productMedia) {
        plan.push({ element: productMedia, origin: 'left', delay: 0 });
      }
      if (productDetails) {
        plan.push({
          element: productDetails,
          origin: 'right',
          delay: REVEAL_STAGGER
        });
      }
      return plan;
    }

    plan = cardRevealPlan(root, heading, ['product-card', '.product-card']);
    if (plan.length > (heading ? 1 : 0)) {
      return plan;
    }

    if (/footer/i.test(identity)) {
      var footerGroups = findRevealItems(
        root,
        '.footer-content > .group-block > .group-block-content > *, .footer-content > .group-block-content > *',
        4
      );
      if (footerGroups.length) {
        return footerGroups.map(function (item, index) {
          return {
            element: item,
            origin: 'up',
            delay: index * REVEAL_STAGGER
          };
        });
      }
    }

    return singleReveal(fallback);
  }

  function prepareReveal(state) {
    var element = chooseRevealElement(state.root);
    if (!element || element.hasAttribute('data-os-motion-revealed')) return;

    var explicitItems = element.matches('[data-os-motion="group"]')
      ? Array.from(element.querySelectorAll('[data-os-motion="item"]')).slice(0, 4)
      : [];
    var plan = explicitItems.length
      ? explicitItems.map(function (item, index) {
          return {
            element: item,
            origin: item.getAttribute('data-os-motion-from') || alternatingOrigin(index),
            delay: index * REVEAL_STAGGER
          };
        })
      : automaticRevealPlan(state, element);

    uniqueElements(plan.map(function (entry) { return entry.element; })).forEach(function (
      target
    ) {
      var entry = plan.find(function (candidate) {
        return candidate.element === target;
      });
      prepareRevealTarget(state, target, entry.delay, entry.origin);
    });
  }

  function prepareRevealTarget(state, element, delay, origin) {
    if (!element || element.hasAttribute('data-os-motion-revealed')) return;
    state.revealElements.add(element);
    state.revealDelays.set(element, delay || 0);
    state.revealOrigins.set(element, origin || 'up');

    var rect = element.getBoundingClientRect();
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var alreadyReached = rect.bottom <= 0 ||
      (rect.top <= viewportHeight * 0.9 && rect.bottom > 0);

    if (decorativeMotionDisabled() || alreadyReached || !viewportObserver) {
      element.setAttribute('data-os-motion-revealed', 'true');
      return;
    }

    /* Only an element already below the first viewport is ever hidden. */
    snapshotRevealStyles(element);
    element.setAttribute('data-os-motion-pending', 'true');
    element.style.setProperty('opacity', '0', 'important');
    element.style.setProperty(
      'transform',
      revealTransform(state.revealOrigins.get(element)),
      'important'
    );
    element.style.setProperty('transition-property', 'none', 'important');
    observe(element, { kind: 'reveal', state: state });
  }

  function forceRevealWithin(root) {
    var scope = root && root.nodeType === 1 ? root : document;
    sectionStates.forEach(function (state) {
      state.revealElements.forEach(function (element) {
        if (scope === document || scope === element || scope.contains(element)) {
          revealElement(state, element, false);
        }
      });
    });
  }

  function findWithin(root, selector) {
    if (root.matches && root.matches(selector)) return root;
    return root.querySelector ? root.querySelector(selector) : null;
  }

  function setupTopbar(state) {
    var bar = findWithin(
      state.root,
      '.optispar-topbar[data-os-topbar-version="8"], .optispar-topbar'
    );
    if (!bar || bar.hasAttribute('data-os-motion-topbar')) return;

    var slides = Array.from(bar.querySelectorAll('[data-os-topbar-slide]'));
    if (slides.length < 2) return;

    var previous = window.__optisparTopbarControllerV8;
    if (previous && previous.bar !== bar && typeof previous.destroy === 'function') {
      previous.destroy();
    } else if (previous && previous.bar === bar && typeof previous.destroy === 'function') {
      previous.destroy();
    }

    bar.querySelectorAll(
      '.optispar-topbar__controls, .os-topbar-controls, [data-os-topbar-controls]'
    ).forEach(function (controls) {
      controls.remove();
    });

    var topbarAbort = new AbortController();
    var mobileTopbarQuery = window.matchMedia('(max-width: 767.98px)');
    var activeIndex = Math.max(
      0,
      slides.findIndex(function (slide) {
        return slide.classList.contains('is-active');
      })
    );
    var rotationTimer = 0;
    var touchTimer = 0;
    var focusPaused = false;
    var touchPaused = false;
    var topbarDestroyed = false;
    var configuredInterval = Number.parseInt(
      bar.getAttribute('data-os-topbar-interval'),
      10
    );
    var rotationInterval = Number.isFinite(configuredInterval) && configuredInterval >= 3000
      ? configuredInterval
      : TOPBAR_INTERVAL;

    bar.setAttribute('data-os-motion-topbar', VERSION);
    bar.style.setProperty('--os-topbar-interval', rotationInterval + 'ms');

    function activate(index) {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach(function (slide, slideIndex) {
        var active = slideIndex === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (slide.matches('a[href]')) {
          if (active) slide.removeAttribute('tabindex');
          else slide.setAttribute('tabindex', '-1');
        }
      });
    }

    function clearRotation() {
      if (!rotationTimer) return;
      window.clearTimeout(rotationTimer);
      rotationTimer = 0;
    }

    function canRotate() {
      return !topbarDestroyed &&
        mobileTopbarQuery.matches &&
        !decorativeMotionDisabled() &&
        !focusPaused &&
        !touchPaused &&
        !document.hidden &&
        bar.isConnected;
    }

    function scheduleRotation() {
      clearRotation();
      if (!canRotate()) return;
      rotationTimer = window.setTimeout(function () {
        rotationTimer = 0;
        if (!canRotate()) return;
        activate(activeIndex + 1);
        scheduleRotation();
      }, rotationInterval);
    }

    function syncMotionPreference() {
      clearRotation();
      if (!mobileTopbarQuery.matches) {
        activeIndex = 0;
        slides.forEach(function (slide, index) {
          slide.classList.toggle('is-active', index === 0);
          slide.setAttribute('aria-hidden', 'false');
          if (slide.matches('a[href]')) slide.removeAttribute('tabindex');
        });
        return;
      }
      if (decorativeMotionDisabled()) {
        activate(0);
        return;
      }
      activate(activeIndex);
      scheduleRotation();
    }

    function pauseForTouch(event) {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
      touchPaused = true;
      clearRotation();
      if (touchTimer) window.clearTimeout(touchTimer);
      touchTimer = window.setTimeout(function () {
        touchTimer = 0;
        touchPaused = false;
        scheduleRotation();
      }, TOPBAR_TOUCH_PAUSE);
    }

    bar.addEventListener('focusin', function () {
      focusPaused = true;
      clearRotation();
    }, { signal: topbarAbort.signal });

    bar.addEventListener('focusout', function () {
      window.setTimeout(function () {
        if (topbarDestroyed || bar.contains(document.activeElement)) return;
        focusPaused = false;
        scheduleRotation();
      }, 0);
    }, { signal: topbarAbort.signal });

    bar.addEventListener('pointerdown', pauseForTouch, {
      passive: true,
      signal: topbarAbort.signal
    });
    bar.addEventListener('pointerup', pauseForTouch, {
      passive: true,
      signal: topbarAbort.signal
    });
    bar.addEventListener('pointercancel', pauseForTouch, {
      passive: true,
      signal: topbarAbort.signal
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) clearRotation();
      else scheduleRotation();
    }, { signal: topbarAbort.signal });

    reducedMotionQuery.addEventListener('change', syncMotionPreference, {
      signal: topbarAbort.signal
    });
    mobileTopbarQuery.addEventListener('change', syncMotionPreference, {
      signal: topbarAbort.signal
    });
    if (connection && typeof connection.addEventListener === 'function') {
      connection.addEventListener('change', syncMotionPreference, {
        signal: topbarAbort.signal
      });
    }

    var controller = {
      bar: bar,
      destroy: function () {
        if (topbarDestroyed) return;
        topbarDestroyed = true;
        clearRotation();
        if (touchTimer) window.clearTimeout(touchTimer);
        topbarAbort.abort();
        bar.removeAttribute('data-os-motion-topbar');
        if (window.__optisparTopbarControllerV8 === controller) {
          window.__optisparTopbarControllerV8 = null;
        }
      }
    };

    window.__optisparTopbarControllerV8 = controller;
    state.cleanups.push(controller.destroy);
    activate(activeIndex);
    syncMotionPreference();
  }

  var previousIcon =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m15 18-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
  var nextIcon =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9 18 6-6-6-6" stroke-linecap="round" stroke-linejoin="round"></path></svg>';

  function createReviewButton(className, label, icon) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'os-review-arrow ' + className;
    button.setAttribute('aria-label', label);
    button.innerHTML = icon;
    return button;
  }

  function canonicalReviewSlides(wrapper) {
    var seen = new Set();
    var slides = [];

    Array.from(wrapper.children).forEach(function (slide, domIndex) {
      if (!slide.classList.contains('swiper-slide')) return;
      if (slide.classList.contains('swiper-slide-duplicate')) return;

      var index = slide.getAttribute('data-swiper-slide-index');
      var key = index !== null && index !== '' ? 'index:' + index : 'dom:' + domIndex;
      if (seen.has(key)) return;
      seen.add(key);
      if (slides.length < REVIEW_MAX) slides.push(slide);
    });

    return slides;
  }

  function ensureReviewControls(review) {
    var widget = review.widget;
    var holder = widget.querySelector('.reputon-carousel-holder');
    var controls = widget.querySelector('.os-review-controls');

    if (!controls) {
      controls = document.createElement('div');
      controls.className = 'os-review-controls';
      controls.appendChild(
        createReviewButton('os-review-prev', 'Vorherige Bewertung', previousIcon)
      );
      var status = document.createElement('span');
      status.className = 'os-review-status';
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      controls.appendChild(status);
      controls.appendChild(
        createReviewButton('os-review-next', 'Nächste Bewertung', nextIcon)
      );
      if (holder) holder.insertAdjacentElement('afterend', controls);
      else widget.appendChild(controls);
      controls.setAttribute('data-os-motion-owned', 'true');
    } else {
      if (controls._osReviewResizeObserver) {
        controls._osReviewResizeObserver.disconnect();
        controls._osReviewResizeObserver = null;
      }

      /* The controls belong to Optispar. Cloning only these buttons removes
         handlers from older Optispar versions without touching Reputon. */
      ['.os-review-prev', '.os-review-next'].forEach(function (selector) {
        var oldButton = controls.querySelector(selector);
        if (!oldButton || oldButton.dataset.osMotionControl === VERSION) return;
        var cleanButton = oldButton.cloneNode(true);
        oldButton.replaceWith(cleanButton);
      });
    }

    controls.dataset.osReviewControls = 'motion-native';
    controls.dataset.osNativeReviewControls = 'motion-native';

    var previous = controls.querySelector('.os-review-prev');
    var next = controls.querySelector('.os-review-next');
    var statusElement = controls.querySelector('.os-review-status');
    if (previous) previous.dataset.osMotionControl = VERSION;
    if (next) next.dataset.osMotionControl = VERSION;

    review.controls = controls;
    review.previousButton = previous;
    review.nextButton = next;
    review.statusElement = statusElement;
    controls._osReviewWrapper = review.wrapper;
  }

  function updateReviewCounter(review) {
    var loaded = Math.min(review.limit, review.total);
    var text = loaded >= review.total
      ? review.total + ' Bewertungen'
      : loaded + ' von ' + review.total + ' Bewertungen geladen';

    if (review.statusElement) {
      review.statusElement.textContent = text;
      review.statusElement.setAttribute(
        'aria-label',
        loaded + ' von ' + review.total + ' Bewertungen geladen'
      );
    }
    review.widget.setAttribute('data-os-reviews-loaded', String(loaded));
    review.widget.setAttribute('data-os-reviews-total', String(review.total));
  }

  function applyReviewLimit(review) {
    review.slides.forEach(function (slide, index) {
      var visible = index < review.limit;
      slide.classList.toggle('optispar-review-hidden', !visible);
      slide.classList.remove('os-review-duplicate', 'os-review-outside-limit');
      slide.style.setProperty('scroll-snap-align', 'start');
      if (visible) {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('inert', '');
      }
    });

    var sharedState = review.widget._osReviewState || {};
    review.widget._osReviewState = sharedState;
    sharedState.initialized = true;
    sharedState.limit = review.limit;
    sharedState.total = review.total;
    sharedState.slides = review.slides;
    sharedState.wrapper = review.wrapper;
    sharedState.userInteracted = false;
    sharedState.dragging = false;

    updateReviewCounter(review);
    updateReviewControls(review);
  }

  function updateReviewControls(review) {
    var atStart = review.wrapper.scrollLeft <= 2;
    var atEnd = review.wrapper.scrollLeft + review.wrapper.clientWidth >=
      review.wrapper.scrollWidth - 2;
    if (review.previousButton) review.previousButton.disabled = atStart;
    if (review.nextButton) {
      review.nextButton.disabled = atEnd && review.limit >= review.total;
    }
  }

  function observeReviewEnd(review) {
    if (review.observedEnd) {
      unobserve(review.sectionState, review.observedEnd);
      review.observedEnd = null;
    }
    if (review.limit >= review.total || !viewportObserver) return;
    var lastVisible = review.slides[Math.max(0, review.limit - 1)];
    if (!lastVisible) return;
    review.observedEnd = lastVisible;
    observe(lastVisible, {
      kind: 'review',
      state: review.sectionState,
      review: review
    });
  }

  function reviewStep(review) {
    var first = review.slides[0];
    if (!first) return review.wrapper.clientWidth;
    var styles = window.getComputedStyle(review.wrapper);
    var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return first.getBoundingClientRect().width + gap;
  }

  function scrollReview(review, direction) {
    if (direction > 0) {
      var atVisibleEnd = review.wrapper.scrollLeft + review.wrapper.clientWidth >=
        review.wrapper.scrollWidth - 3;
      if (atVisibleEnd && review.limit < review.total) review.revealNextBatch();
    }
    review.wrapper.scrollTo({
      left: review.wrapper.scrollLeft + direction * reviewStep(review),
      behavior: decorativeMotionDisabled() ? 'auto' : 'smooth'
    });
  }

  function initializeReviews(state) {
    if (state.review && state.review.ready) return true;
    var widget = findWithin(state.root, REVIEW_WIDGET_SELECTOR);
    var wrapper = widget && widget.querySelector('.swiper-wrapper');
    if (!widget || !wrapper) return false;

    var slides = canonicalReviewSlides(wrapper);
    if (!slides.length) return false;

    var review = {
      ready: true,
      sectionState: state,
      widget: widget,
      wrapper: wrapper,
      slides: slides,
      total: Math.min(REVIEW_MAX, slides.length),
      limit: Math.min(REVIEW_INITIAL, slides.length),
      controls: null,
      previousButton: null,
      nextButton: null,
      statusElement: null,
      observedEnd: null,
      originalTouchAction: wrapper.style.getPropertyValue('touch-action'),
      originalTouchPriority: wrapper.style.getPropertyPriority('touch-action'),
      swiper: null,
      swiperAllowTouchMove: null,
      revealNextBatch: null,
      sectionClassAdded: !state.root.classList.contains('os-motion-reviews')
    };
    state.review = review;
    state.root.classList.add('os-motion-reviews');

    widget.setAttribute('data-autoscroll', 'false');
    wrapper.classList.remove('os-review-pointer-ready', 'os-review-dragging');
    wrapper.style.setProperty('touch-action', 'pan-x pan-y pinch-zoom', 'important');
    wrapper.style.setProperty('-webkit-overflow-scrolling', 'touch');

    var swiperElement = widget.querySelector('.swiper');
    var swiper = swiperElement && swiperElement.swiper;
    if (swiper) {
      review.swiper = swiper;
      review.swiperAllowTouchMove = swiper.allowTouchMove;
      swiper.allowTouchMove = false;
      try {
        if (swiper.autoplay && swiper.autoplay.stop) swiper.autoplay.stop();
      } catch (error) {}
    }

    ensureReviewControls(review);

    review.revealNextBatch = function () {
      if (review.limit >= review.total || state.destroyed) return false;
      review.limit = Math.min(review.total, review.limit + REVIEW_BATCH);
      applyReviewLimit(review);
      observeReviewEnd(review);
      return true;
    };

    if (review.previousButton) {
      review.previousButton.addEventListener('click', function () {
        scrollReview(review, -1);
      }, { signal: state.abort.signal });
    }
    if (review.nextButton) {
      review.nextButton.addEventListener('click', function () {
        scrollReview(review, 1);
      }, { signal: state.abort.signal });
    }

    var scrollUpdatePending = false;
    wrapper.addEventListener('scroll', function () {
      if (scrollUpdatePending) return;
      scrollUpdatePending = true;
      requestFrame(state, function () {
        scrollUpdatePending = false;
        updateReviewControls(review);
      });
    }, { passive: true, signal: state.abort.signal });

    applyReviewLimit(review);
    observeReviewEnd(review);

    state.cleanups.push(function () {
      if (review.observedEnd) unobserve(state, review.observedEnd);
      review.slides.forEach(function (slide) {
        slide.classList.remove('optispar-review-hidden', 'os-review-dragging');
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('inert');
        slide.style.removeProperty('scroll-snap-align');
      });
      if (review.originalTouchAction) {
        review.wrapper.style.setProperty(
          'touch-action',
          review.originalTouchAction,
          review.originalTouchPriority
        );
      } else {
        review.wrapper.style.removeProperty('touch-action');
      }
      review.wrapper.style.removeProperty('-webkit-overflow-scrolling');
      if (review.swiper && review.swiperAllowTouchMove !== null) {
        review.swiper.allowTouchMove = review.swiperAllowTouchMove;
      }
      if (review.sectionClassAdded) {
        review.sectionState.root.classList.remove('os-motion-reviews');
      }
      if (
        review.controls &&
        review.controls.getAttribute('data-os-motion-owned') === 'true'
      ) {
        review.controls.remove();
      }
    });
    return true;
  }

  function setupReviews(state) {
    var hasWidget = Boolean(findWithin(state.root, REVIEW_WIDGET_SELECTOR));
    var hinted = (state.root.id || '').indexOf(REVIEW_SECTION_HINT) !== -1 ||
      state.root.classList.contains('optispar-review-section');
    if (!hasWidget && !hinted) return;
    if (initializeReviews(state)) return;

    /* Reputon renders asynchronously. This observer is scoped to its one
       Shopify section and disconnects immediately after readiness. */
    var readinessObserver = new MutationObserver(function () {
      if (!initializeReviews(state)) return;
      readinessObserver.disconnect();
      if (readinessTimer) clearTimer(state, readinessTimer);
    });
    readinessObserver.observe(state.root, { childList: true, subtree: true });
    var readinessTimer = setTimer(state, function () {
      readinessObserver.disconnect();
    }, 12000);
    state.cleanups.push(function () {
      readinessObserver.disconnect();
    });
  }

  /* Quick-view delivery promise. The element owns one minute-aligned timer and
     pauses it while the tab is hidden. Business-day arithmetic includes the
     nationwide German holidays and the additional NRW holidays. */
  function registerDeliveryPromiseElement() {
    if (
      !window.customElements ||
      !window.HTMLElement ||
      window.customElements.get('os-delivery-promise')
    ) return;

    function numberAttribute(element, names, fallback, minimum, maximum) {
      var raw = null;
      names.some(function (name) {
        raw = element.getAttribute(name);
        return raw !== null;
      });
      var value = Number.parseInt(raw, 10);
      if (!Number.isFinite(value)) value = fallback;
      return Math.min(maximum, Math.max(minimum, value));
    }

    function timeZoneAttribute(element) {
      var zone = element.getAttribute('data-timezone') ||
        element.getAttribute('data-time-zone') ||
        element.getAttribute('data-os-time-zone') ||
        'Europe/Berlin';
      try {
        new Intl.DateTimeFormat('de-DE', { timeZone: zone }).format();
        return zone;
      } catch (error) {
        return 'Europe/Berlin';
      }
    }

    function zonedParts(date, timeZone) {
      var parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      }).formatToParts(date);
      var result = {};
      parts.forEach(function (part) {
        if (part.type !== 'literal') result[part.type] = Number(part.value);
      });
      return result;
    }

    function zonedInstant(calendar, hour, minute, timeZone) {
      var target = Date.UTC(
        calendar.year,
        calendar.month - 1,
        calendar.day,
        hour,
        minute,
        0
      );
      var guess = target;
      for (var index = 0; index < 3; index += 1) {
        var parts = zonedParts(new Date(guess), timeZone);
        var represented = Date.UTC(
          parts.year,
          parts.month - 1,
          parts.day,
          parts.hour,
          parts.minute,
          parts.second
        );
        guess += target - represented;
      }
      return new Date(guess);
    }

    function calendarFromParts(parts) {
      return {
        year: parts.year,
        month: parts.month,
        day: parts.day
      };
    }

    function addCalendarDays(calendar, amount) {
      var date = new Date(Date.UTC(
        calendar.year,
        calendar.month - 1,
        calendar.day + amount,
        12
      ));
      return {
        year: date.getUTCFullYear(),
        month: date.getUTCMonth() + 1,
        day: date.getUTCDate()
      };
    }

    var holidayCache = new Map();

    function calendarKey(calendar) {
      return calendar.year + '-' + twoDigits(calendar.month) + '-' +
        twoDigits(calendar.day);
    }

    /* Gregorian Easter Sunday (Meeus/Jones/Butcher algorithm). */
    function easterSunday(year) {
      var a = year % 19;
      var b = Math.floor(year / 100);
      var c = year % 100;
      var d = Math.floor(b / 4);
      var e = b % 4;
      var f = Math.floor((b + 8) / 25);
      var g = Math.floor((b - f + 1) / 3);
      var h = (19 * a + b - d - g + 15) % 30;
      var i = Math.floor(c / 4);
      var k = c % 4;
      var l = (32 + 2 * e + 2 * i - h - k) % 7;
      var m = Math.floor((a + 11 * h + 22 * l) / 451);
      var month = Math.floor((h + l - 7 * m + 114) / 31);
      return {
        year: year,
        month: month,
        day: ((h + l - 7 * m + 114) % 31) + 1
      };
    }

    function holidayKeys(year, region) {
      var cacheKey = region + ':' + year;
      if (holidayCache.has(cacheKey)) return holidayCache.get(cacheKey);
      var holidays = new Set([
        year + '-01-01',
        year + '-05-01',
        year + '-10-03',
        year + '-12-25',
        year + '-12-26'
      ]);
      var easter = easterSunday(year);
      [-2, 1, 39, 50].forEach(function (offset) {
        holidays.add(calendarKey(addCalendarDays(easter, offset)));
      });
      if (region === 'DE-NW') {
        holidays.add(calendarKey(addCalendarDays(easter, 60)));
        holidays.add(year + '-11-01');
      }
      holidayCache.set(cacheKey, holidays);
      return holidays;
    }

    function isBusinessDay(calendar, region) {
      var day = new Date(Date.UTC(
        calendar.year,
        calendar.month - 1,
        calendar.day,
        12
      )).getUTCDay();
      return day > 0 && day < 6 &&
        !holidayKeys(calendar.year, region).has(calendarKey(calendar));
    }

    function nextBusinessDay(calendar, region) {
      var result = addCalendarDays(calendar, 1);
      while (!isBusinessDay(result, region)) {
        result = addCalendarDays(result, 1);
      }
      return result;
    }

    function addBusinessDays(calendar, amount, region) {
      var result = calendar;
      var remaining = Math.max(0, amount);
      while (remaining > 0) {
        result = addCalendarDays(result, 1);
        if (isBusinessDay(result, region)) remaining -= 1;
      }
      return result;
    }

    function calendarDate(calendar) {
      return new Date(Date.UTC(
        calendar.year,
        calendar.month - 1,
        calendar.day,
        12
      ));
    }

    function twoDigits(value) {
      return String(value).padStart(2, '0');
    }

    function shortDate(calendar, includeYear) {
      var value = twoDigits(calendar.day) + '.' +
        twoDigits(calendar.month) + '.';
      return includeYear ? value + calendar.year : value;
    }

    function dateRange(first, last) {
      var includeYear = first.year !== last.year;
      return shortDate(first, includeYear) + ' – ' +
        shortDate(last, includeYear);
    }

    function weekdayDate(calendar) {
      return new Intl.DateTimeFormat('de-DE', {
        weekday: 'long',
        day: '2-digit',
        month: '2-digit',
        timeZone: 'UTC'
      }).format(calendarDate(calendar));
    }

    function countdownText(minutes) {
      var hours = Math.floor(minutes / 60);
      var remaining = minutes % 60;
      var duration = [];
      if (hours) duration.push(hours + ' Std.');
      if (remaining || !hours) duration.push(remaining + ' Min.');
      return 'Noch ' + duration.join(' ') + ' – Versand heute';
    }

    class OptisparDeliveryPromise extends HTMLElement {
      constructor() {
        super();
        this._osDeliveryAbort = null;
        this._osDeliveryTimer = 0;
        this._osDeliveryTick = this._osDeliveryRefresh.bind(this);
        this._osDeliveryVisibility = this._osDeliveryVisibilityChange.bind(this);
      }

      connectedCallback() {
        if (this._osDeliveryAbort) return;
        this._osDeliveryAbort = new AbortController();
        this._osDeliveryCountdown = this.querySelector(
          '[data-os-delivery-countdown]'
        );
        this._osDeliveryDispatch = this.querySelector(
          '[data-os-delivery-dispatch]'
        );
        this._osDeliveryArrival = this.querySelector(
          '[data-os-delivery-arrival]'
        );
        this._osDeliveryTimeZone = timeZoneAttribute(this);
        this._osDeliveryRegion = (
          this.getAttribute('data-region') ||
          this.getAttribute('data-os-region') ||
          'DE-NW'
        ).trim().toUpperCase();
        this._osDeliveryCutoff = numberAttribute(
          this,
          ['data-cutoff-hour', 'data-os-cutoff-hour'],
          12,
          0,
          23
        );
        this._osDeliveryTransitMin = numberAttribute(
          this,
          ['data-transit-min', 'data-os-transit-min'],
          1,
          0,
          30
        );
        this._osDeliveryTransitMax = Math.max(
          this._osDeliveryTransitMin,
          numberAttribute(
            this,
            ['data-transit-max', 'data-os-transit-max'],
            3,
            0,
            30
          )
        );
        document.addEventListener(
          'visibilitychange',
          this._osDeliveryVisibility,
          { signal: this._osDeliveryAbort.signal }
        );
        this._osDeliveryRefresh();
      }

      disconnectedCallback() {
        this._osDeliveryStopTimer();
        if (this._osDeliveryAbort) this._osDeliveryAbort.abort();
        this._osDeliveryAbort = null;
      }

      _osDeliveryStopTimer() {
        if (!this._osDeliveryTimer) return;
        window.clearTimeout(this._osDeliveryTimer);
        this._osDeliveryTimer = 0;
      }

      _osDeliverySchedule() {
        this._osDeliveryStopTimer();
        if (!this.isConnected || document.hidden) return;
        var delay = 60000 - (Date.now() % 60000) + 50;
        this._osDeliveryTimer = window.setTimeout(
          this._osDeliveryTick,
          delay
        );
      }

      _osDeliveryVisibilityChange() {
        if (document.hidden) {
          this._osDeliveryStopTimer();
          return;
        }
        this._osDeliveryRefresh();
      }

      _osDeliveryRefresh() {
        this._osDeliveryTimer = 0;
        if (!this.isConnected || !this._osDeliveryAbort) return;
        var now = new Date();
        var parts = zonedParts(now, this._osDeliveryTimeZone);
        var today = calendarFromParts(parts);
        var cutoff = zonedInstant(
          today,
          this._osDeliveryCutoff,
          0,
          this._osDeliveryTimeZone
        );
        var shipsToday = isBusinessDay(today, this._osDeliveryRegion) &&
          now < cutoff;
        var shippingDay = shipsToday
          ? today
          : nextBusinessDay(today, this._osDeliveryRegion);
        var earliest = addBusinessDays(
          shippingDay,
          this._osDeliveryTransitMin,
          this._osDeliveryRegion
        );
        var latest = addBusinessDays(
          shippingDay,
          this._osDeliveryTransitMax,
          this._osDeliveryRegion
        );
        var cutoffLabel = twoDigits(this._osDeliveryCutoff) + ':00 Uhr';

        this.setAttribute(
          'data-os-delivery-state',
          shipsToday ? 'today' : 'next-business-day'
        );
        if (this._osDeliveryCountdown) {
          this._osDeliveryCountdown.textContent = shipsToday
            ? countdownText(Math.max(1, Math.ceil((cutoff - now) / 60000)))
            : 'Nächster Versand: ' + weekdayDate(shippingDay);
        }
        if (this._osDeliveryDispatch) {
          this._osDeliveryDispatch.textContent = shipsToday
            ? 'Bestellung bis ' + cutoffLabel + ': Versand heute'
            : 'Versand am ' + weekdayDate(shippingDay);
        }
        if (this._osDeliveryArrival) {
          this._osDeliveryArrival.textContent = dateRange(earliest, latest);
        }
        this._osDeliverySchedule();
      }
    }

    window.customElements.define(
      'os-delivery-promise',
      OptisparDeliveryPromise
    );
  }

  registerDeliveryPromiseElement();

  /* Product-page purchase polish. The Shopify AI blocks keep ownership of
     their delivery calculation and quantity synchronization; this layer only
     shortens the customer-facing copy and adds keyboard semantics. */
  function setupProductPurchaseExperience() {
    var main = document.querySelector('#MainContent[data-template="product"]');
    if (!main) return;

    var details = main.querySelector(
      ':scope > .shopify-section > .product-information ' +
      '[data-testid="product-information-details"]'
    );
    if (!details) return;

    var content = details.querySelector(
      ':scope > .group-block > .group-block-content'
    );
    if (!content) return;

    var tax = content.querySelector(
      ':scope > product-price + .text-block.custom'
    );
    if (tax && tax.dataset.osTaxCopy !== '1') {
      var taxText = tax.querySelector('p') || tax;
      var syncTaxCopy = function () {
        if (taxText.textContent !== '(inkl. MwSt.)') {
          taxText.textContent = '(inkl. MwSt.)';
        }
      };
      syncTaxCopy();
      tax._osTaxObserver = new MutationObserver(syncTaxCopy);
      tax._osTaxObserver.observe(taxText, {
        childList: true,
        characterData: true,
        subtree: true
      });
      tax.dataset.osTaxCopy = '1';
    }

    var deliveryBlock = content.querySelector(
      ':scope > .shopify-block:has([data-countdown]):has([data-delivery])'
    );
    var deliveryCountdown = deliveryBlock &&
      deliveryBlock.querySelector('[data-countdown]');
    var delivery = deliveryCountdown && deliveryCountdown.parentElement;
    if (delivery) {
      delivery.dataset.textHeader = 'Gratis Versand';
      delivery.dataset.textShipsToday =
        'Bestellung bis 12 Uhr · Versand heute';
      delivery.dataset.textShipsOn = 'Nächster Versand:';
      delivery.dataset.textDeliveryPrefix = 'Bei Ihnen:';

      var headerText = delivery.querySelector('[data-header-text]');
      if (headerText) headerText.textContent = delivery.dataset.textHeader;

      var countdownText = delivery.querySelector('[data-countdown-text]');
      if (countdownText) {
        var shortenCountdown = function () {
          var value = (countdownText.textContent || '').trim();
          var match = value.match(
            /(\d+)\s+Std\.\s+(\d+)\s+Min\.\s+\d+\s+Sek\./
          );
          if (!match) return;
          var hours = Number(match[1]);
          var minutes = Number(match[2]);
          var parts = [];
          if (hours > 0) parts.push(hours + ' Std.');
          if (minutes > 0 || hours === 0) parts.push(minutes + ' Min.');
          var concise = 'Noch ' + parts.join(' ') + ' – Versand heute';
          if (countdownText.textContent !== concise) {
            countdownText.textContent = concise;
          }
        };

        shortenCountdown();
        if (!countdownText._osProductCountdownObserver) {
          countdownText._osProductCountdownObserver = new MutationObserver(
            shortenCountdown
          );
          countdownText._osProductCountdownObserver.observe(countdownText, {
            childList: true,
            characterData: true,
            subtree: true
          });
        }
      }
    }

    var discountBlock = content.querySelector(
      ':scope > .shopify-block:has([data-quantity])'
    );
    if (discountBlock) {
      var discountHeading = discountBlock.querySelector('h3');
      if (
        discountHeading &&
        !discountHeading.querySelector('.os-quantity-discount__hint')
      ) {
        var discountHint = document.createElement('span');
        discountHint.className = 'os-quantity-discount__hint';
        discountHint.textContent = 'bis zu 15 % sparen';
        discountHeading.appendChild(discountHint);
      }
    }

    if (discountBlock && discountBlock.dataset.osAccessibleTiers !== '1') {
      var tiers = Array.from(
        discountBlock.querySelectorAll('[data-quantity]')
      );
      var syncTierAccessibility = function () {
        tiers.forEach(function (tier) {
          tier.setAttribute(
            'aria-pressed',
            tier.classList.contains('active') ? 'true' : 'false'
          );
        });
      };

      tiers.forEach(function (tier) {
        var quantity = tier.getAttribute('data-quantity') || '';
        var label = (tier.textContent || '').replace(/\s+/g, ' ').trim();
        tier.setAttribute('role', 'button');
        tier.setAttribute('tabindex', '0');
        tier.setAttribute(
          'aria-label',
          label || ('Mengenrabatt für ' + quantity + ' Stück')
        );
        tier.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          tier.click();
        }, { signal: globalAbort.signal });
      });

      syncTierAccessibility();
      discountBlock._osTierObserver = new MutationObserver(
        syncTierAccessibility
      );
      discountBlock._osTierObserver.observe(discountBlock, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true
      });
      discountBlock.dataset.osAccessibleTiers = '1';
    }

    /* === OS QUANTITY DISCOUNT SYNC === */
    if (discountBlock) {
      var osQuantityInput = content.querySelector(
        'quantity-selector-component input[name="quantity"]'
      );

      if (
        osQuantityInput &&
        osQuantityInput.dataset.osDiscountSync !== '1'
      ) {
        var osQuantitySelector = osQuantityInput.closest(
          'quantity-selector-component'
        );
        var osMinusButton = osQuantitySelector &&
          osQuantitySelector.querySelector('button[name="minus"]');
        var osPlusButton = osQuantitySelector &&
          osQuantitySelector.querySelector('button[name="plus"]');

        var syncDiscountQuantityControls = function () {
          var currentQuantity = Math.max(
            1,
            parseInt(osQuantityInput.value, 10) || 1
          );
          var minimumQuantity = parseInt(
            osQuantityInput.min ||
            osQuantityInput.dataset.min ||
            '1',
            10
          ) || 1;
          var maximumQuantity = parseInt(
            osQuantityInput.max ||
            osQuantityInput.dataset.max ||
            '0',
            10
          ) || 0;

          if (osMinusButton) {
            osMinusButton.disabled = currentQuantity <= minimumQuantity;
          }
          if (osPlusButton) {
            osPlusButton.disabled =
              maximumQuantity > 0 && currentQuantity >= maximumQuantity;
          }

          Array.from(
            discountBlock.querySelectorAll('[data-quantity]')
          ).forEach(function (tier) {
            var tierQuantity = parseInt(
              tier.getAttribute('data-quantity'),
              10
            ) || 1;
            var isActive = tierQuantity === 4
              ? currentQuantity >= 4
              : currentQuantity === tierQuantity;
            tier.classList.toggle('active', isActive);
            tier.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
        };

        var scheduleDiscountQuantitySync = function () {
          window.requestAnimationFrame(function () {
            syncDiscountQuantityControls();
            window.setTimeout(syncDiscountQuantityControls, 80);
          });
        };

        osQuantityInput.addEventListener(
          'input',
          syncDiscountQuantityControls,
          { signal: globalAbort.signal }
        );
        osQuantityInput.addEventListener(
          'change',
          syncDiscountQuantityControls,
          { signal: globalAbort.signal }
        );

        discountBlock.addEventListener('click', function (event) {
          if (!event.target.closest('[data-quantity]')) return;
          scheduleDiscountQuantitySync();
        }, { signal: globalAbort.signal });

        [osMinusButton, osPlusButton].forEach(function (button) {
          if (!button) return;
          button.addEventListener(
            'click',
            scheduleDiscountQuantitySync,
            { signal: globalAbort.signal }
          );
        });

        osQuantityInput.dataset.osDiscountSync = '1';
        syncDiscountQuantityControls();
      }
    }

    var benefitGrid = details.querySelector('.optispar-benefit-grid');
    if (benefitGrid && benefitGrid.dataset.osProductCopy !== '1') {
      var benefitCopy = [
        { title: 'Gratis Versand', label: 'aus Deutschland' },
        { title: '30 Tage', label: 'Widerrufsrecht' },
        { title: 'Sicher', label: 'bezahlen' }
      ];
      Array.from(
        benefitGrid.querySelectorAll('.optispar-benefit')
      ).forEach(function (benefit, index) {
        var copy = benefitCopy[index];
        if (!copy) return;
        var strong = benefit.querySelector('strong');
        if (strong) strong.textContent = copy.title;
        Array.from(benefit.childNodes).forEach(function (node) {
          if (node.nodeType === 3) node.remove();
        });
        var label = benefit.querySelector('.optispar-benefit__label');
        if (!label) {
          label = document.createElement('span');
          label.className = 'optispar-benefit__label';
          benefit.appendChild(label);
        }
        label.textContent = copy.label;
      });
      benefitGrid.dataset.osProductCopy = '1';
    }

    var sticky = main.querySelector(
      ':scope > .shopify-section > sticky-add-to-cart'
    );
    if (sticky) {
      /* Keep one reversible activation line for every viewport: once the
         original buy button passes behind the sticky header, show the compact
         bar. Crossing that same line upward hides it again. */
      if (sticky._osRecommendationsObserver) {
        sticky._osRecommendationsObserver.disconnect();
        sticky._osRecommendationsObserver = null;
      }
      sticky.classList.remove('os-near-product-end', 'os-sticky-latched');

      var primaryBuyButton = details.querySelector(
        '.buy-buttons-block .add-to-cart-button'
      );
      if (primaryBuyButton && !sticky._osStickyScrollBound) {
        var stickyFrame = 0;
        var stickyBar = sticky.querySelector('.sticky-add-to-cart__bar');
        var setStickyVisibility = function (show) {
          var nextState = show ? 'true' : 'false';
          if (sticky.dataset.osScrollVisible === nextState) return;
          sticky.dataset.osScrollVisible = nextState;
          sticky.classList.toggle('os-sticky-visible', show);
          sticky.classList.remove('os-sticky-latched');
          if (stickyBar) {
            stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
            stickyBar.toggleAttribute('inert', !show);
          }
        };
        var checkStickyThreshold = function () {
          stickyFrame = 0;
          if (!primaryBuyButton.isConnected || !sticky.isConnected) return;
          var headerHeight = parseFloat(
            window.getComputedStyle(document.body)
              .getPropertyValue('--header-group-height')
          ) || 0;
          var activationLine = Math.max(
            0,
            Math.min(headerHeight, window.innerHeight * 0.25)
          );
          setStickyVisibility(
            primaryBuyButton.getBoundingClientRect().bottom <= activationLine
          );
        };
        var queueStickyThreshold = function () {
          if (stickyFrame) return;
          stickyFrame = window.requestAnimationFrame(checkStickyThreshold);
        };

        sticky.dataset.osScrollReady = 'true';
        sticky._osStickyScrollBound = true;
        window.addEventListener('scroll', queueStickyThreshold, {
          passive: true,
          signal: globalAbort.signal
        });
        window.addEventListener('resize', queueStickyThreshold, {
          passive: true,
          signal: globalAbort.signal
        });
        checkStickyThreshold();
      }
    }
  }

  function initializeSection(root) {
    if (!root || sectionStates.has(root) || destroyed) return;
    var state = createSectionState(root);
    sectionStates.set(root, state);
    prepareReveal(state);
    setupTopbar(state);
    setupReviews(state);
  }

  function sectionRootsWithin(root) {
    if (!root || root === document) {
      return Array.from(document.querySelectorAll(SECTION_SELECTOR));
    }
    if (root.nodeType !== 1) return [];
    if (root.matches(SECTION_SELECTOR)) return [root];
    return Array.from(root.querySelectorAll(SECTION_SELECTOR));
  }

  function init(root) {
    if (destroyed) return;
    setupProductPurchaseExperience();
    sectionRootsWithin(root || document).forEach(initializeSection);
  }

  function destroySection(root) {
    var state = sectionStates.get(root);
    if (!state || state.destroyed) return;
    state.destroyed = true;

    state.observed.forEach(function (element) {
      if (viewportObserver) viewportObserver.unobserve(element);
      observedTargets.delete(element);
    });
    state.observed.clear();
    state.frames.forEach(function (frame) {
      window.cancelAnimationFrame(frame);
    });
    state.frames.clear();
    state.timers.forEach(function (timer) {
      window.clearTimeout(timer);
    });
    state.timers.clear();
    state.abort.abort();

    state.cleanups.slice().reverse().forEach(function (cleanup) {
      try { cleanup(); } catch (error) {}
    });
    state.revealElements.forEach(function (element) {
      clearRevealStyles(element);
      element.removeAttribute('data-os-motion-revealed');
    });
    state.revealDelays.clear();
    state.revealOrigins.clear();
    sectionStates.delete(root);
  }

  function destroy(root) {
    if (root && root !== document) {
      sectionRootsWithin(root).forEach(destroySection);
      return;
    }
    if (destroyed) return;
    destroyed = true;
    Array.from(sectionStates.keys()).forEach(destroySection);
    if (viewportObserver) viewportObserver.disconnect();
    globalAbort.abort();
    documentElement.classList.toggle('os-save-data', initialSaveDataClass);
    if (initialSaveDataAttribute === null) {
      documentElement.removeAttribute('data-os-save-data');
    } else {
      documentElement.setAttribute('data-os-save-data', initialSaveDataAttribute);
    }
  }

  function onReady() {
    if (destroyed) return;
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  }, { signal: globalAbort.signal });

  document.addEventListener('shopify:section:unload', function (event) {
    sectionRootsWithin(event.target).forEach(destroySection);
  }, { signal: globalAbort.signal });

  document.addEventListener('shopify:section:select', function (event) {
    init(event.target);
    forceRevealWithin(event.target);
  }, { signal: globalAbort.signal });

  document.addEventListener('shopify:block:select', function (event) {
    forceRevealWithin(event.target);
  }, { signal: globalAbort.signal });

  document.addEventListener('shopify:section:reorder', function (event) {
    init(event.target);
    forceRevealWithin(event.target);
  }, { signal: globalAbort.signal });

  reducedMotionQuery.addEventListener('change', function () {
    if (reducedMotionQuery.matches) forceRevealWithin(document);
  }, { signal: globalAbort.signal });

  if (connection && typeof connection.addEventListener === 'function') {
    connection.addEventListener('change', function () {
      syncSaveDataState();
      if (saveDataEnabled()) forceRevealWithin(document);
    }, { signal: globalAbort.signal });
  }

  syncSaveDataState();

  window.OptisparMotion = {
    version: VERSION,
    init: init,
    destroy: destroy,
    reveal: forceRevealWithin
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, {
      once: true,
      signal: globalAbort.signal
    });
  } else {
    onReady();
  }
})();
